import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sparkles, X, Loader2, Heart, Shuffle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { usePublicGallery, categoryDisplayNames } from "@/hooks/usePublicGallery";
import { useGalleryLikes } from "@/hooks/useGalleryLikes";
import { cn } from "@/lib/utils";

// Fallback static images when database is empty
const fallbackImages = [
  { id: "f1", url: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=600&fit=crop", category: "portrait", prompt: "Portrait photoréaliste d'une femme élégante avec éclairage studio", likes_count: 0 },
  { id: "f2", url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=600&fit=crop", category: "landscape", prompt: "Paysage montagneux au coucher de soleil, lumière dorée", likes_count: 0 },
  { id: "f3", url: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&h=600&fit=crop", category: "fantasy", prompt: "Château flottant dans les nuages, style fantastique", likes_count: 0 },
  { id: "f4", url: "https://images.unsplash.com/photo-1633477189729-9290b3261d0a?w=600&h=600&fit=crop", category: "anime", prompt: "Personnage 3D style Pixar, mascotte mignonne", likes_count: 0 },
  { id: "f5", url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop", category: "portrait", prompt: "Portrait masculin cinématique, éclairage dramatique", likes_count: 0 },
  { id: "f6", url: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&h=600&fit=crop", category: "landscape", prompt: "Forêt enchantée avec rayons de lumière mystiques", likes_count: 0 },
  { id: "f7", url: "https://images.unsplash.com/photo-1541701494587-cb58502866ab?w=600&h=600&fit=crop", category: "abstract", prompt: "Art digital abstrait avec néons et formes géométriques", likes_count: 0 },
  { id: "f8", url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=600&fit=crop", category: "portrait", prompt: "Portrait lifestyle, sourire naturel, lumière douce", likes_count: 0 },
];

const categories = ["Tous", "Portraits", "Paysages", "Fantasy", "Anime", "Abstrait", "Autres"];

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState("Tous");
  const [selectedImage, setSelectedImage] = useState<{ id: string; url: string; category: string; prompt: string; likes_count: number } | null>(null);
  const navigate = useNavigate();
  
  const { data: dbImages, isLoading } = usePublicGallery(undefined, 100);

  // Combine database images with fallback
  const allImages = dbImages && dbImages.length > 0
    ? dbImages.map(img => ({
        id: img.id,
        url: img.image_url,
        category: img.category,
        prompt: img.prompt,
        likes_count: img.likes_count,
      }))
    : fallbackImages;

  const imageIds = allImages.filter(img => !img.id.startsWith("f")).map(img => img.id);
  const { toggleLike, isLiked, isLoading: likeLoading } = useGalleryLikes(imageIds);

  // Map category display name to database category
  const getCategoryKey = (displayName: string): string => {
    const entry = Object.entries(categoryDisplayNames).find(([, v]) => v === displayName);
    return entry ? entry[0] : displayName.toLowerCase();
  };

  const filteredImages = selectedCategory === "Tous" 
    ? allImages 
    : allImages.filter(img => {
        const categoryKey = getCategoryKey(selectedCategory);
        return img.category === categoryKey || img.category === selectedCategory;
      });

  const getCategoryDisplayName = (category: string): string => {
    return categoryDisplayNames[category] || category;
  };

  const handleLike = (e: React.MouseEvent, imageId: string) => {
    e.stopPropagation();
    if (!imageId.startsWith("f")) {
      toggleLike(imageId);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-12 text-center">
        <div className="container mx-auto px-6">
          <span className="inline-block text-sm font-medium text-primary mb-4">
            Galerie
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Découvrez la puissance de{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">
              Floop
            </span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Explorez notre collection d'images générées par notre communauté. 
            Votez pour vos créations préférées !
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="pb-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-gradient-floop text-white"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-16">
        <div className="container mx-auto px-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Aucune image dans cette catégorie pour le moment.</p>
            </div>
          ) : (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className="mb-4 break-inside-avoid cursor-pointer group"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="relative rounded-2xl overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.prompt}
                      className="w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    {/* Like button overlay */}
                    <button
                      onClick={(e) => handleLike(e, image.id)}
                      disabled={likeLoading || image.id.startsWith("f")}
                      className={cn(
                        "absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-all",
                        isLiked(image.id)
                          ? "bg-primary/90 text-white"
                          : "bg-background/70 text-foreground hover:bg-background/90"
                      )}
                    >
                      <Heart className={cn("w-4 h-4", isLiked(image.id) && "fill-current")} />
                    </button>
                    {/* Likes count */}
                    {image.likes_count > 0 && (
                      <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-background/70 backdrop-blur-sm text-xs font-medium text-foreground flex items-center gap-1">
                        <Heart className="w-3 h-3 fill-primary text-primary" />
                        {image.likes_count}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <span className="text-xs text-primary font-medium">
                          {getCategoryDisplayName(image.category)}
                        </span>
                        <p className="text-sm text-foreground line-clamp-2 mt-1">{image.prompt}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-card border-border p-0">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute right-4 top-4 z-10 p-2 rounded-full bg-background/80 hover:bg-background transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedImage && (
            <div className="grid md:grid-cols-2 gap-0">
              <div className="aspect-square relative">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.prompt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                      {getCategoryDisplayName(selectedImage.category)}
                    </span>
                    <button
                      onClick={(e) => handleLike(e, selectedImage.id)}
                      disabled={likeLoading || selectedImage.id.startsWith("f")}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-full transition-all",
                        isLiked(selectedImage.id)
                          ? "bg-primary text-white"
                          : "bg-secondary text-foreground hover:bg-secondary/80"
                      )}
                    >
                      <Heart className={cn("w-4 h-4", isLiked(selectedImage.id) && "fill-current")} />
                      <span className="text-sm font-medium">{selectedImage.likes_count}</span>
                    </button>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Prompt utilisé</h3>
                  <p className="text-muted-foreground bg-secondary/50 p-4 rounded-xl">
                    "{selectedImage.prompt}"
                  </p>
                </div>
                <div className="mt-6 flex gap-3">
                  <Link to="/create" state={{ prompt: selectedImage.prompt }} className="flex-1">
                    <Button className="w-full bg-gradient-floop hover:opacity-90 text-white rounded-xl">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Créer similaire
                    </Button>
                  </Link>
                  <Button 
                    variant="secondary"
                    className="flex-1 rounded-xl"
                    onClick={() => {
                      setSelectedImage(null);
                      navigate("/remix", { state: { imageUrl: selectedImage.url, prompt: selectedImage.prompt } });
                    }}
                  >
                    <Shuffle className="w-4 h-4 mr-2" />
                    Remixer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA */}
      <section className="py-16 bg-gradient-floop/10">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
            Créez vos propres chefs-d'œuvre
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Commencez gratuitement et laissez libre cours à votre créativité.
          </p>
          <Link to="/create">
            <Button size="lg" className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-8">
              <Sparkles className="w-5 h-5 mr-2" />
              Commencer à créer
            </Button>
          </Link>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Gallery;
